import axios from "axios";
import moment from "moment";

import { apiConfig } from "../config/authConfig";

import { ICirbaBolUploadRequest, ILoad, ILoadCloseRequest, ILoadCreateRequest, ILoadHistory, ILoadHistoryAddRequest, ILoadLight, ILoadMaterial } from "../types/load";
import { IMaterialInventory, IMaterialInventoryAssignment, IMaterialInventoryExportRequest, IMaterialInventoryLoad } from "../types/material";
import { ISelectOption } from "../types/list";

import { CredentialManager } from "../classes/credentialManager";

export namespace LoadService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Load.
	 * @param data Load response data from an API.
	 **********/
	export function mapLoad(data: any) {
		let load: ILoad = {
			id: data.id,
			customerId: data.customerId,
			customerName: data.customerName,
			vendorId: data.vendorId,
			vendorName: data.vendorName,
			transporterId: data.transporterId,
			transporterName: data.transporterName,
			trailerLengthId: data.trailerLengthId,
			trailerId: data.trailerId,
			trailerNumber: data.trailerNumber,
			arrivalDate: data.arrivalDate,
			scheduledPickupDate: data.scheduledPickupDate,
			pickupDate: data.pickupDate,
			materials: data.materials,
			status: data.status,
			hwiNumber: data.hwiNumber,
			manifestNumbers: data.manifestNumbers,
			sealNumber: data.sealNumber,
			vendorLoadId: data.vendorLoadId,
			isRedwood: data.isRedwood,
			loadDataSentToVendor: data.loadDataSentToVendor,
			signedBolDocumentId: data.signedBolDocumentId
		}

		return load;
	}

	/**********
	 * Convert an array of api response objects to an array of Loads.
	 * @param data Load data from an API response.
	 **********/
	export function mapLoads(data: any) {
		let loads: ILoad[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				loads.push(mapLoad(item));
			})
		}

		return loads;
	}


	/**********
	 * Convert one api response object to one Load.
	 * @param data Load response data from an API.
	 **********/
	export function mapLoadLight(data: any) {
		let load: ILoadLight = {
			id: data.id,
			hwiNumber: data.hwiNumber ? data.hwiNumber.replace("HWI-", "") : "",
			manifestNumbers: data.manifestNumbers,
			customerName: data.customerName,
			vendorName: data.vendorName,
			trailerNumber: data.trailerNumber,
			plantWeight: data.plantWeight,
			vendorWeight: data.vendorWeight,
			scheduledPickupDate: data.scheduledPickupDate,
			pickupDate: data.pickupDate,
			status: data.status,
			loadDataSentToVendor: data.loadDataSentToVendor,
			signedBolDocumentId: data.signedBolDocumentId
		}

		return load;
	}

	/**********
	 * Convert an array of api response objects to an array of Loads.
	 * @param data Load data from an API response.
	 **********/
	export function mapLoadLightList(data: any) {
		let loads: ILoadLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				loads.push(mapLoadLight(item));
			})
		}

		return loads;
	}

	/**********
	 * Gets all loads.
	 **********/
	export async function getLoads(start: Date, customers: number[]) {
		const s = String(start.getFullYear()) + String(start.getMonth() + 1).padStart(2, '0') + String(start.getDate()).padStart(2, '0');
		//const e = String(end.getFullYear()) + String(end.getMonth() + 1).padStart(2, '0') + String(end.getDate()).padStart(2, '0');

		const apiResponse = axios.get(
			// apiConfig.default + "/load/all/" + s + "/" + e,
			apiConfig.default + "/load/all/" + s + "/" + customers.toString(),
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all loads by employee id.
	 * @param employeeId
	 **********/
	export async function getLoadsByEmployee(employeeId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/load/byemployee/" + employeeId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a load.
	 * @param loadId The id of the load.
	 **********/
	export async function getLoad(loadId: string) {
		const employeeResponse = axios.get(
			apiConfig.default + "/load/" + loadId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return employeeResponse;
	}

	/**********
	 * Add a new load.
	 * @param request The load's details.
	 **********/
	export async function addLoad(request: ILoadCreateRequest) {
		const apiResponse = axios.put(
			apiConfig.default + "/load",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a new material to a load.
	 * @param details The material's details.
	 **********/
	export async function addMaterial(loadId: number, details: ILoadMaterial) {
		const apiResponse = axios.put(
			apiConfig.default + "/load/" + loadId + "/material",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove a material from a TILS load.
	 * @param loadId The transporter's id.
	 * @param loadMaterialId material record id.
	 **********/
	export async function removeMaterial(loadId: string, loadMaterialId: number) {
		const apiResponse = axios.delete(
			apiConfig.default + "/load/" + loadId + "/material/" + loadMaterialId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Close a load, setting it's status to "Ready"
	 * @param loadId The bol number, or load id (same thing).
	 * @param sealNumber The seal number to add to the closed load.
	 **********/
	export async function close(loadId: number, sealNumber: string) {
		const request: ILoadCloseRequest = { sealNumber: sealNumber };
		const apiResponse = axios.put(
			apiConfig.default + `/load/${loadId}/close`,
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Break the seal of a "Ready" load, removing the seal number and
	 * returning it to "Loading" status.
	 * @param loadId The bol number, or load id (same thing).
	 **********/
	export async function breakSeal(loadId: number) {
		const apiResponse = axios.put(
			apiConfig.default + `/load/${loadId}/breakSeal`,
			"fake payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Change the status of a "Ready" load back to "Loading", allowing its 
	 * materials to be modified. This is primarily for cancelled Redwood shipments.
	 * @param loadId The bol number, or load id (same thing).
	 **********/
	export async function returnToLoading(loadId: number) {
		const apiResponse = axios.put(
			apiConfig.default + `/load/${loadId}/returnToLoading`,
			"fake payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Sets scheduled pickup date on a load, also setting it's status to "Ready"
	 * @param loadId The load id.
	 **********/
	export async function schedule(loadId: number, pickupDate: Date, bypassVendor?: string) {
		const apiResponse = axios.put(
			apiConfig.default + `/load/${loadId}/schedule/${moment(pickupDate).format("yyyyMMDD")}`,
			{ bypassVendor: bypassVendor },
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Sets confirmed pickup date on a load, also setting it's status to "Shipped"
	 * @param loadId The bol number, or load id (same thing).
	 * @param pickupDate The date to set as the confirmed pickup date.
	 * @param signedBolDocument An optional signed BOL document to upload.
	 **********/
	export async function pickup(loadId: number, pickupDate: Date, signedBolDocument?: File) {
		const apiResponse = axios.put(
			apiConfig.default + `/load/${loadId}/pickup/${moment(pickupDate).format("yyyyMMDD")}`,
			{ signedBolDocument: signedBolDocument },
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'multipart/form-data',
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Deletes a load, it's details, it's shipment, and related shipment details records.
	 * @param id shipment id.
	 **********/
	export async function deleteLoad(id: number, reason: string) {
		const payload = {
			loadId: id,
			deletedReason: reason
		};
		const response = axios.post(
			apiConfig.default + "/load/",
			payload,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Added to facilitate the manual conversion of loads that did not get closed before migration. 
	 * The shipment details records were not created and needed to be created "manually" so we added a little form to do them one by one, using the load ID 
	 * @param loadId The bol number, or load id (same thing).
	 **********/
	export async function convertLoadDetails(loadId: string) {
		const apiResponse = axios.put(
			apiConfig.default + `/load/${loadId}/convertdetails`,
			"payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Schedules pickup date with Redwood Materials. Also sets the load's status to "Ready".
	 * @param loadId The bol number, or load id (same thing).
	 * @param pickupDate The date to schedule the pickup for.
	 **********/
	export async function scheduleRedwood(loadId: number, pickupDate: Date) {
		const apiResponse = axios.post(
			apiConfig.default + `/load/${loadId}/scheduleredwood/${moment(pickupDate).format("yyyyMMDD")}`,
			"fake payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets the status of a previously submitted Redwood shipment by the ID provided upon posting.
	 * @param loadId The id of the load previously submitted to Redwood.
	 **********/
	export async function getRedwoodStatus(loadId: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/${loadId}/redwoodstatus`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets the Bill of Lading document provided by Redwood.
	 * @param loadId The id of the load previously submitted to Redwood.
	 **********/
	export async function getRedwoodBol(loadId: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/${loadId}/redwoodbol`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Creates a new material inventory record.
	 * @param inventory The material inventory record to create.	 
	 **********/
	export async function addMaterialInventory(inventory: IMaterialInventory) {
		const apiResponse = axios.put(
			apiConfig.default + `/load/materialinventory`,
			inventory,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Updates an existing material inventory record.
	 * @param inventory The material inventory record to update.	 
	 **********/
	export async function updateMaterialInventory(inventory: IMaterialInventory) {
		const apiResponse = axios.post(
			apiConfig.default + `/load/materialinventory`,
			inventory,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all material inventory records.
	 **********/
	export async function getMaterialInventories() {
		const apiResponse = axios.get(
			apiConfig.default + `/load/materialinventory/all`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all material inventory records by date range.
	 **********/
	export async function getMaterialInventoriesByDateRange(startDate: string, endDate: string) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/materialinventory/dates/${startDate}/${endDate}`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a material inventory record by its ID.
	 * @param id The ID of the material inventory record to retrieve.
	 **********/
	export async function getMaterialInventory(id: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/materialinventory/${id}`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all material inventory records associated with a customer.
	 * @param id The ID of the customer to retrieve material inventories for.
	 **********/
	export async function getMaterialInventoriesByCustomerId(id: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/materialinventory/customer/${id}`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all material inventory records associated with a customer but not assigned to any load.
	 * @param id The ID of the customer to retrieve material inventories for.
	 **********/
	export async function getUnassignedMaterialInventoriesByCustomerId(id: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/materialinventory/customer/${id}/unassigned`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all material inventory records associated with a load.
	 * @param id The ID of the load to retrieve material inventories for.
	 **********/
	export async function getMaterialInventoriesByLoadId(id: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/materialinventory/load/${id}`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Convert one api response object to one MaterialInventory.
	 * @param data MaterialInventory response data from an API.
	 **********/
	export function mapMaterialInventory(data: any) {
		let inventory: IMaterialInventory = {
			id: data.id,
			customerId: data.customerId,
			customerName: data.customerName,
			vendorId: data.vendorId,
			materialId: data.materialId,
			materialName: data.materialName,
			isHazardous: data.isHazardous,
			containerId: data.containerId,
			containerName: data.containerName,
			customerLocationId: data.customerLocationId,
			customerLocationName: data.customerLocationName,
			weight: data.weight,
			tareWeight: data.tareWeight,
			grossWeight: data.grossWeight,
			uomId: data.uomId,
			uomName: data.uomName,
			uomLbsConversion: data.uomLbsConversion,
			createdBy: data.createdBy,
			creationDate: data.creationDate ? new Date(data.creationDate) : undefined,
			remoteShipmentId: data.remoteShipmentId,
			remoteShipmentStatus: data.remoteShipmentStatus,
			remoteShipmentPosition: data.remoteShipmentPosition,
			quantity: data.quantity,
			modifiedDate: data.modifiedDate ? new Date(data.modifiedDate) : undefined
		}

		return inventory;
	}

	/**********
	 * Convert an array of api response objects to an array of MaterialInventories.
	 * @param data MaterialInventory data from an API response.
	 **********/
	export function mapMaterialInventories(data: any) {
		let inventories: IMaterialInventory[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				inventories.push(mapMaterialInventory(item));
			})
		}

		return inventories;
	}

	/**********
	 * Convert an array of api response objects to an array of ISelectOptions for MaterialInventories.
	 * @param data MaterialInventory data from an API response.
	 **********/
	export function mapMaterialInventoryOptions(data: any) {
		let options: ISelectOption[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				options.push({
					name: `${item.containerName} (${item.materialName})`,
					value: item.id
				} as ISelectOption);
			})
		}

		return options;
	}

	/**********
	 * Deletes a material inventory record.
	 * @param id The ID of the material inventory record to delete.
	 **********/
	export async function deleteMaterialInventory(id: number) {
		const apiResponse = axios.delete(
			apiConfig.default + `/load/materialinventory/${id}`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a collection of loads in "Loading" status belonging to the provided customer that material inventories can be assigned to.
	 * @param id The ID of the customer to retrieve loads for.
	 **********/
	export async function getAssignableLoadsForMaterialInventoryByCustomerId(id: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/assignable/materialinventory/customer/${id}`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets information about the load that a material inventory is assigned to.
	 * @param id The ID of the material inventory to retrieve the assigned load for.
	 **********/
	export async function getAssignedMaterialInventoryLoad(id: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/materialinventory/${id}/assigned`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Convert one api response object to one MaterialInventoryLoad.
	 * @param data MaterialInventoryLoad response data from an API.
	 **********/
	export function mapMaterialInventoryLoad(data: any) {
		let load: IMaterialInventoryLoad = {
			loadId: data.loadId,
			hwiNumber: data.hwiNumber,
			customerName: data.customerName,
			trailerNumber: data.trailerNumber,
			status: data.status,
		}

		return load;
	}

	/**********
	 * Convert an array of api response objects to an array of MaterialInventoryLoads.
	 * @param data MaterialInventoryLoad data from an API response.
	 **********/
	export function mapMaterialInventoryLoads(data: any) {
		let loads: IMaterialInventoryLoad[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				loads.push(mapMaterialInventoryLoad(item));
			})
		}

		return loads;
	}

	/**********
	 * Assigns a material inventory to a load at the provided trailer position.
	 * @param assignment The material inventory assignment details.
	 **********/
	export async function addMaterialInventoryToLoad(assignment: IMaterialInventoryAssignment) {
		const apiResponse = axios.put(
			apiConfig.default + "/load/materialinventory/assign",
			assignment,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Unassigns a material inventory from its referenced load.
	 * @param assignment The material inventory (un)assignment details.
	 **********/
	export async function removeMaterialInventoryFromLoad(assignment: IMaterialInventoryAssignment) {
		const apiResponse = axios.post(
			apiConfig.default + "/load/materialinventory/unassign",
			assignment,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Sends search criteria to the API to export the results to an Excel document..
	 * @param request The search criteria.
	 **********/
	export async function exportMaterialInvendoryListToExcel(request: IMaterialInventoryExportRequest) {
		const apiResponse = axios.put(
			apiConfig.default + "/load/materialinventory/export",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Creates a Cirba work order from information about the load and sends the document to them in an email.
	 * @param loadId The ID of the load.
	 **********/
	export async function sendWorkOrderToCirba(loadId: number) {
		const apiResponse = axios.post(
			apiConfig.default + `/load/${loadId}/cirba/workorder`,
			'fake payload',
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Uploads a BOL document recieved from Cirba to Azure file storage.
	 * @param request The upload request.
	 **********/
	export async function uploadCirbaBol(request: ICirbaBolUploadRequest) {
		const apiResponse = axios.post(
			apiConfig.default + `/load/${request.loadId}/cirba/bol`,
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'multipart/form-data',
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Downloads the Cirba-provided Bill of Lading document from Azure File Storage.
	 * @param loadId The id of the load previously submitted to Cirba.
	 **********/
	export async function getCirbaBol(loadId: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/${loadId}/cirba/bol`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Exports the load material list to an Excel document.
	 * @param loadId The id of the load.
	 **********/
	export async function exportLoadMaterialListToExcel(loadId: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/load/${loadId}/material/export`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Convert one api response object to one Load history comment.
	 * @param data LoadHistory response data from an API.
	 **********/
	export function mapLoadHistory(data: any): ILoadHistory {
		let history: ILoadHistory = {
			id: data.id,
			loadId: data.loadId,
			type: data.type,
			username: data.username,
			description: data.description,
			date: new Date(data.date),
			oldStatus: data.oldStatus,
			newStatus: data.newStatus,
			category: data.category,
			action: data.action,
			misc: data.misc,
		};

		return history;
	}

	/**********
	 * Convert an array of api response objects to an array of Load history comments.
	 * @param data LoadHistory data from an API response.
	 **********/
	export function mapLoadHistories(data: any): ILoadHistory[] {
		let comments: ILoadHistory[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				comments.push(mapLoadHistory(item));
			})
		}

		return comments;
	}

	/**********
	 * Gets comments from a load's change history.
	 * @param id The load id.
	 **********/
	export async function getLoadHistory(id: string) {
		const response = axios.get(
			apiConfig.default + "/load/" + id + "/history",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Adds a comment to the load's change history.
	 * @param request The comment to add.
	 **********/
	export async function addHistoryComment(id: string, request: ILoadHistoryAddRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/load/" + id + "/history",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Emails an exported load sheet to the vendor's contact email addresses.
	 * @param id The load id.
	 **********/
	export async function emailLoadSheetToVendor(id: number) {
		const response = axios.get(
			apiConfig.default + "/load/" + id + "/vendor/emailLoadSheet",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Notifies the transporter that a load has been scheduled for pickup.
	 * @param id The load id.
	 **********/
	export async function notifyTransporterOfLoadScheduled(id: number) {
		const response = axios.get(
			apiConfig.default + "/load/" + id + "/transporter/notify",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}
}
